import { useCallback, useEffect, useState } from "react";
import { ShieldCheck, RefreshCw, Loader2, AlertTriangle } from "lucide-react";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";

const fmtAt = (iso) => {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleString("en-CA", { month: "short", day: "numeric", hour: "numeric", minute: "2-digit" });
  } catch { return iso; }
};

/**
 * SecurityLogPanel — admin Settings → Security tab. Every login with its IP
 * + device. Rows glow amber when the same IP has been used by other users
 * (possible shared device / co-worker's phone).
 */
const SecurityLogPanel = () => {
  const [rows, setRows] = useState(null);
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    setBusy(true);
    try {
      const { data } = await api.get("/settings/security-log");
      setRows(Array.isArray(data) ? data : []);
    } catch { setRows([]); }
    finally { setBusy(false); }
  }, []);
  useEffect(() => { load(); }, [load]);

  return (
    <div className="space-y-3" data-testid="security-log-panel">
      <div className="flex items-start gap-3 rounded-lg border border-white/10 bg-white/[0.03] px-4 py-3">
        <ShieldCheck className="mt-0.5 h-4 w-4 text-[#4f6fe8]" />
        <div className="flex-1">
          <p className="text-sm font-semibold text-white">Security log</p>
          <p className="text-xs text-white/40">
            Every login with its IP address and device. An amber chip means other users have signed in or
            punched from the same IP. Admins are also alerted automatically when someone clocks out from a
            different IP than they clocked in.
          </p>
        </div>
        <Button onClick={load} disabled={busy} variant="outline" size="sm" data-testid="security-log-refresh"
          className="border-white/20 bg-transparent text-white hover:bg-white/10">
          {busy ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <RefreshCw className="h-3.5 w-3.5" />}
        </Button>
      </div>

      {rows === null ? (
        <div className="flex justify-center py-8 text-white/50"><Loader2 className="h-5 w-5 animate-spin" /></div>
      ) : rows.length === 0 ? (
        <p className="py-6 text-center font-mono text-[10px] uppercase tracking-widest text-white/40" data-testid="security-log-empty">
          No logins recorded yet
        </p>
      ) : (
        <ul className="max-h-[46vh] space-y-1.5 overflow-y-auto pr-1" data-testid="security-log-list">
          {rows.map((r) => (
            <li
              key={r.log_id}
              data-testid={`security-log-${r.log_id}`}
              className={`flex flex-wrap items-center gap-x-3 gap-y-1 rounded-md border px-3 py-2 text-xs ${
                r.shared_ip ? "border-amber-500/40 bg-amber-500/[0.06]" : "border-white/10 bg-white/[0.02]"
              }`}
            >
              <span className="font-mono text-white/50">{fmtAt(r.at)}</span>
              <span className="font-semibold text-white/90">{r.name || r.email || "—"}</span>
              <span className="font-mono text-white/60">{r.ip || "no ip"}</span>
              {r.shared_ip && (
                <span className="inline-flex items-center gap-1 rounded-full border border-amber-500/50 bg-amber-500/15 px-1.5 py-0.5 font-mono text-[9px] uppercase tracking-widest text-amber-700 dark:text-amber-300" data-testid={`security-shared-${r.log_id}`}>
                  <AlertTriangle className="h-2.5 w-2.5" /> shared IP
                </span>
              )}
              <span className="min-w-0 flex-1 truncate text-right font-mono text-[10px] text-white/35" title={r.ua}>
                {r.detail || r.kind}
              </span>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
};

export default SecurityLogPanel;
